import React, { useState, useEffect } from 'react';
import AdminDashboard from './AdminDashboard';
import { userAPI } from '../../services/api';
import { useAuth } from '../../services/AuthContext';
import UserModal from '../../components/Modals/UserModal';

const AdminAdmins = () => {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll({ role: 'admin' });
      setAdmins(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to load administrators list');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (user) => {
    if (user.id === currentUser?.id) {
      alert('You cannot block your own account');
      return;
    }
    try {
      const newStatus = user.status === 'blocked' ? 'active' : 'blocked';
      await userAPI.update(user.id, { status: newStatus });
      fetchAdmins();
    } catch (err) {
      console.error('Error toggling status:', err);
      alert('Failed to update user status');
    }
  };

  const filteredAdmins = admins.filter(admin => 
    admin.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <AdminDashboard title="Security & Administration">
      <div className="p-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
          <div className="relative w-full md:w-96">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]">search</span>
            <input
              type="text"
              placeholder="Search administrators..."
              className="w-full pl-12 pr-4 py-3 bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-2xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all font-medium placeholder-[var(--text-secondary)]/50"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <button 
              onClick={fetchAdmins}
              className="flex items-center gap-2 px-6 py-3 bg-[var(--surface-white)] border border-[var(--border-light)] rounded-2xl text-sm font-bold text-[var(--text-secondary)] hover:bg-[var(--off-white)] transition-all shadow-sm"
            >
              <span className="material-symbols-outlined text-lg">refresh</span>
              Refresh
            </button>
            <button 
              onClick={() => { setSelectedUser(null); setIsModalOpen(true); }}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-8 py-3 bg-purple-600 text-white rounded-2xl text-sm font-black hover:bg-purple-700 transition-all shadow-md shadow-purple-500/20"
            >
              <span className="material-symbols-outlined text-lg">person_add</span>
              Add Admin
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[var(--text-secondary)] font-bold">Accessing secure data...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-3xl p-8 text-center text-red-600 dark:text-red-400">
            <span className="material-symbols-outlined text-4xl mb-2">gpp_maybe</span>
            <p className="font-bold">{error}</p>
            <button onClick={fetchAdmins} className="mt-4 underline font-bold">Retry</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[var(--border-light)]">
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4">Administrator</th>
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4">Role Path</th>
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4">Security</th>
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4">Created</th>
                  <th className="pb-4 font-bold text-[var(--text-secondary)]/50 uppercase text-xs tracking-widest px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-light)]/30">
                {filteredAdmins.length > 0 ? (
                  filteredAdmins.map((admin) => (
                    <tr key={admin.id} className="group hover:bg-[var(--off-white)]/50 transition-colors">
                      <td className="py-5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center overflow-hidden border-2 border-[var(--surface-white)] shadow-sm font-black text-purple-400">
                            {admin.avatarUrl ? (
                              <img src={`${process.env.REACT_APP_API_URL}${admin.avatarUrl}`} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="material-symbols-outlined">admin_panel_settings</span>
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-[var(--text-primary)]">
                              {admin.name}
                              {admin.id === currentUser?.id && (
                                <span className="ml-2 text-[8px] bg-purple-600 text-white px-1.5 py-0.5 rounded uppercase tracking-tighter align-middle">You</span>
                              )}
                            </div>
                            <div className="text-xs text-[var(--text-secondary)] font-medium">{admin.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-5 px-4 text-sm font-medium text-purple-600">
                        System Full Access
                      </td>
                      <td className="py-5 px-4 text-sm">
                        <span className={`px-3 py-1 rounded-full font-bold text-[10px] uppercase tracking-wider flex items-center gap-1 w-fit ${
                          admin.status === 'blocked' 
                            ? 'bg-red-500/10 text-red-600 dark:text-red-400' 
                            : 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
                        }`}>
                          <span className="material-symbols-outlined text-[12px]">
                            {admin.status === 'blocked' ? 'block' : 'verified_user'}
                          </span>
                          {admin.status === 'blocked' ? 'Blocked' : 'Active'}
                        </span>
                      </td>
                      <td className="py-5 px-4 text-sm font-bold text-[var(--text-secondary)]/70">
                        {formatDate(admin.createdAt)}
                      </td>
                      <td className="py-5 px-1 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={() => toggleStatus(admin)}
                            className={`p-2 rounded-xl transition-all ${
                              admin.status === 'blocked'
                                ? 'text-green-500 hover:bg-green-500/10'
                                : 'text-red-400 hover:bg-red-500/10'
                            } disabled:opacity-20`}
                            disabled={admin.id === currentUser?.id}
                            title={admin.status === 'blocked' ? 'Unblock user' : 'Block user'}
                          >
                            <span className="material-symbols-outlined">
                              {admin.status === 'blocked' ? 'person_check' : 'person_off'}
                            </span>
                          </button>
                          <button 
                            onClick={() => { setSelectedUser(admin); setIsModalOpen(true); }}
                            className="p-2 text-[var(--text-secondary)]/50 hover:text-purple-600 hover:bg-purple-500/10 rounded-xl transition-all disabled:opacity-20"
                            disabled={admin.id === currentUser?.id}
                            title="Edit user"
                          >
                            <span className="material-symbols-outlined">settings_suggest</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-20 text-center text-[var(--text-secondary)]/50 font-medium italic">
                      No other administrators found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <UserModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          user={selectedUser} 
          onSave={fetchAdmins} 
          defaultRole="admin" 
        />
      </div>
    </AdminDashboard>
  );
};

export default AdminAdmins;
